// Client-side paging for lists that are ALREADY fully loaded (#924).
//
// Two different problems wear the word "pagination" here, and they need
// different answers:
//
//   - Archive surfaces (admin users/orgs, a repo's audit) page the SERVER with an
//     opaque cursor and a "Load more" button. Rows arrive a slice at a time.
//     Nothing here applies — that's `lib/paged.ts` (`usePagedQuery` + <LoadMore/>).
//   - Overview surfaces (the Home panels) fetch ONE bounded top-N slice; those
//     endpoints are tiles, not archives, and their `next_cursor` is always null
//     (`lib/overview.ts`). Every row is in memory. There is nothing to fetch, so
//     a "Load more" would be a lie — what's needed is a way to walk the rows
//     already held without the panel growing without bound.
//
// This is the second one.

import { createMemo, createSignal } from 'solid-js';

export type Pagination<T> = {
  /** Current page, 1-based. Clamped — it can never point past the last page. */
  readonly page: () => number;
  /** Total pages; at least 1, so an empty list still reads "1 of 1". */
  readonly pageCount: () => number;
  /** The rows for the current page. */
  readonly slice: () => readonly T[];
  /** True when the list fits on one page — the caller hides the pager entirely. */
  readonly single: () => boolean;
  readonly next: () => void;
  readonly prev: () => void;
};

/**
 * Page a reactive list. `rows` is an accessor so the pagination re-derives when
 * the underlying data changes — which matters on Home, where an SSE stream
 * patches rows in live.
 *
 * The page number is CLAMPED on read rather than reset on change: if rows drain
 * while you're on the last page (a session finishes, an approval is handled) you
 * land on the new last page instead of an empty one. Sitting on page 1 — the
 * common case — is unaffected either way.
 */
export function createPagination<T>(rows: () => readonly T[], pageSize: number): Pagination<T> {
  const [requested, setRequested] = createSignal(1);

  const pageCount = createMemo(() => Math.max(1, Math.ceil(rows().length / pageSize)));
  const page = createMemo(() => Math.min(Math.max(1, requested()), pageCount()));
  const slice = createMemo(() => {
    const start = (page() - 1) * pageSize;
    return rows().slice(start, start + pageSize);
  });

  return {
    page,
    pageCount,
    slice,
    single: () => pageCount() <= 1,
    next: () => setRequested(Math.min(page() + 1, pageCount())),
    prev: () => setRequested(Math.max(page() - 1, 1)),
  };
}

/**
 * The page-nav shape that `<Pager/>` consumes. A plain `Pagination<T>` already
 * satisfies this (it carries `page`/`pageCount`/`single`/`next`/`prev` and `slice`
 * is dropped), so existing client-side pagers (`routes/Home.tsx`,
 * `routes/Pending.tsx`, `components/FleetQueuePanel.tsx`) need ZERO changes
 * to keep working — the next/prev row is enough on its own. Numbered mode
 * (#5136) is a SEPARATE, wider shape — {@link ServerPageNav} — not a set of
 * extra props on this one.
 */
export type PageNav = Pick<Pagination<unknown>, 'page' | 'pageCount' | 'single' | 'next' | 'prev'>;

/**
 * The numbered-mode shape `<Pager/>` switches on. `goTo` is the ONE
 * discriminant — its presence alone puts `<Pager/>` into numbered mode, so
 * there is no second prop that could disagree with it. `total`/`pageSize` ride
 * on the SAME object `serverPageNav` returns, never passed a second time as
 * sibling props: one source, no `as` casts at the call site.
 */
export type ServerPageNav = PageNav & {
  readonly total: () => number;
  readonly pageSize: () => number;
  readonly goTo: (page: number) => void;
};

/**
 * The numbered-button window (`#5136`). Always shows the first and last page;
 * the current page ± 1; an ellipsis (`'gap'`) wherever a run of pages is
 * skipped. Examples:
 *   page 5 of 20 ⇒ `[1, 'gap', 4, 5, 6, 'gap', 20]`
 *   page 1 of 7  ⇒ `[1, 2, 3, 4, 5, 6, 7]`
 *   page 7 of 7  ⇒ `[1, 'gap', 5, 6, 7]`
 *   1 page       ⇒ `[1]`
 *
 * Pure: same inputs always produce the same output. Caller is responsible for
 * hiding the pager when `pageCount <= 1` (`pagination.single()` is the source
 * of that rule, unchanged).
 */
export function pageWindow(page: number, pageCount: number): (number | 'gap')[] {
  if (pageCount <= 1) return [1];
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const out: (number | 'gap')[] = [];
  // Current ± 1, but never below 2 (page 1 is already in the head) and never
  // above `pageCount - 1` (the last page is already in the tail).
  const from = Math.max(2, page - 1);
  const to = Math.min(pageCount - 1, page + 1);
  out.push(1);
  if (from > 2) out.push('gap');
  for (let p = from; p <= to; p++) out.push(p);
  if (to < pageCount - 1) out.push('gap');
  out.push(pageCount);
  return out;
}

/**
 * True while a numbered server-paged list (#5136) is showing a STALE
 * past-the-end response: zero rows, a page past the first, and a nonzero
 * total proving there genuinely ARE rows — the window between the
 * past-the-end reset effect rewriting `?page=` back to 1 and that reset's
 * OWN page-1 refetch landing (`routes/Plans.tsx`, `CustomPlansPanels.tsx`
 * both run the same effect over their own envelope). `data.page` is the
 * ENVELOPE's own page, not the URL's — `placeholderData` keeps the stale
 * envelope on screen until the reset's refetch resolves, so the envelope's
 * `page` lags the already-rewritten URL by exactly this window.
 *
 * Structural, not `PlanListPage`-typed: `lib/paginate.ts` carries no domain
 * import, so any numbered-mode caller with these three fields can reuse it
 * rather than re-deriving the same three-way check per caller (#5264 review
 * — the two callers above had drifted: one showed a loading skeleton for
 * this window, the other rendered an empty `<ul>` under a `<Pager/>` still
 * announcing the stale total's nonzero "Showing" range).
 */
export function pastTheEndResetting(data: {
  readonly plans: readonly unknown[];
  readonly page: number;
  readonly total: number;
}): boolean {
  return data.plans.length === 0 && data.page > 1 && data.total > 0;
}

/**
 * Adapt a server-driven list (#5136) — total rows + page size + a setter — to
 * the `PageNav` shape `<Pager/>` already takes. `next`/`prev` delegate to
 * `goTo`, clamped to `[1, pageCount]` so a press at either end is a no-op,
 * not a refetch for the same page. `page()` and `pageCount()` are computed
 * from the live `total` so the reader always lands on a real page, the same
 * clamp `createPagination` does for the client case.
 */
export function serverPageNav(o: {
  readonly page: () => number;
  readonly total: () => number;
  readonly pageSize: () => number;
  readonly goTo: (page: number) => void;
}): ServerPageNav {
  const pageCount = (): number => Math.max(1, Math.ceil(o.total() / o.pageSize()));
  const clampedPage = (): number => Math.min(Math.max(1, o.page()), pageCount());
  const clamped = (target: number): number => Math.min(Math.max(1, target), pageCount());
  return {
    page: clampedPage,
    pageCount,
    single: () => pageCount() <= 1,
    next: () => o.goTo(clamped(clampedPage() + 1)),
    prev: () => o.goTo(clamped(clampedPage() - 1)),
    total: o.total,
    pageSize: o.pageSize,
    // Unclamped, matching the old `<Pager/>` prop's pass-through: every caller
    // (`pageWindow`'s numbered buttons) only ever offers an already-valid page.
    goTo: o.goTo,
  };
}
